import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DismissRegular } from "@fluentui/react-icons";
import { format, isValid, startOfDay, startOfMonth, subMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "hooks/useAppTheme";

/** Offered date: the first day of the previous month */
export const getDefaultIssuedFrom = (): Date =>
  startOfMonth(subMonths(new Date(), 1));

/**
 * The issuedFrom query value: the start of the chosen day in the browser's time zone
 * (Latvia for QTG users), so the backend compares against the same instant.
 */
export const toIssuedFromParam = (date: Date): string =>
  startOfDay(date).toISOString();

type Props = {
  open: boolean;
  onCancel: () => void;
  onConfirm: (issuedFrom: Date) => void;
};

/**
 * Asks for the issue date from which the invoices are exported (every MTPL, CASCO and
 * group invoice issued on that day or later), before the export is requested from the backend.
 */
export const ExportInvoicesDialog = ({ open, onCancel, onConfirm }: Props) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const [issuedFrom, setIssuedFrom] = useState<Date | null>(null);

  useEffect(() => {
    if (!open) return;
    setIssuedFrom(getDefaultIssuedFrom());
  }, [open]);

  const validationError = useMemo((): string | null => {
    if (!issuedFrom || !isValid(issuedFrom)) {
      return t("qtg.export.invoices.required", "The date is required");
    }
    if (startOfDay(issuedFrom) > new Date()) {
      return t(
        "qtg.export.invoices.inFuture",
        "The date must not be in the future",
      );
    }
    return null;
  }, [issuedFrom, t]);

  const formatDate = (date?: Date) =>
    date && isValid(date) ? format(date, "dd.MM.yyyy") : "";

  return (
    <Dialog open={open} onOpenChange={(e, data) => !data.open && onCancel()}>
      <DialogSurface style={{ maxWidth: "460px" }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={onCancel}
              />
            }
          >
            {t("qtg.export.invoices.title", "Export invoices")}
          </DialogTitle>
          <DialogContent>
            <p style={{ marginTop: 0 }}>
              {t(
                "qtg.export.invoices.description",
                "Exports every MTPL, CASCO and group invoice issued on the chosen date or later to an Excel file for the Premium Collection Tool. Line invoices of grouped offers are covered by their group invoice.",
              )}
            </p>
            <Field
              data-help="offers-export-invoices-from"
              label={t("qtg.export.invoices.issuedFrom", "Issued from")}
              required
              size="small"
            >
              <DatePicker
                value={issuedFrom}
                onSelectDate={(date) =>
                  setIssuedFrom(date && isValid(date) ? date : null)
                }
                formatDate={formatDate}
                placeholder="DD.MM.YYYY"
                style={{ width: "160px" }}
                size="small"
              />
            </Field>
            {validationError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 8,
                  borderRadius: 4,
                  background: theme.semanticColors.errorBackground,
                  color: theme.semanticColors.errorText,
                }}
              >
                {validationError}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              {t("qtg.common.cancel", "Cancel")}
            </Button>
            <Button
              data-help="offers-export-invoices-confirm"
              appearance="primary"
              disabled={!!validationError}
              onClick={() => {
                if (issuedFrom && !validationError) {
                  onConfirm(issuedFrom);
                }
              }}
            >
              {t("qtg.export.period.confirm", "Export")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
